import React from 'react';

import { Groups, OpenInNew, VpnKey } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormControlLabel, IconButton, Link, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { CopyToClipboardButton } from '@@components';

import {
	ApiKeyModel,
	ClientModel,
	ComponentModel,
	ComponentRuntimeProfileOverride,
	EnvironmentApiKeyModel,
	EnvironmentClient,
	EnvironmentClientModel,
	EnvironmentComponentModel,
	EnvironmentModel,
	RuntimeProfileTier,
} from '../../../api/api';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import WebsiteOriginsEditor from './WebsiteOriginsEditor';
import AttributesList from './AttributesList';
import { ReferencingResourcesSection } from './ReferencingResourceCard';

const ENVIRONMENT_COMPONENT_DETAILS_PANEL_STORAGE_KEY = 'environmentComponentDetailsPanel.width';
const ENVIRONMENT_COMPONENT_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const ENVIRONMENT_COMPONENT_DETAILS_PANEL_MIN_WIDTH = 360;
const ENVIRONMENT_COMPONENT_DETAILS_PANEL_MAX_WIDTH = 1000;
const DEFAULT_RUNTIME_PROFILE = {
	tier: RuntimeProfileTier.Small,
	minInstances: 0,
	maxInstances: 3,
};

interface EnvironmentComponentDetailsPanelProps {
	component: ComponentModel;
	environmentComponent: EnvironmentComponentModel;
	environment?: EnvironmentModel;
	clients?: ClientModel[];
	apiKeys?: ApiKeyModel[];
	onClose: () => void;
	onRefresh?: () => void;
	onClientSelect?: (client: ClientModel, environmentClient?: EnvironmentClientModel, environment?: EnvironmentModel) => void;
	onApiKeySelect?: (apiKey: ApiKeyModel, environmentApiKey?: EnvironmentApiKeyModel, environment?: EnvironmentModel) => void;
	top?: number;
}

export default function EnvironmentComponentDetailsPanel({
	component,
	environmentComponent,
	environment,
	clients = [],
	apiKeys = [],
	onClose,
	onRefresh,
	onClientSelect,
	onApiKeySelect,
	top = 0,
}: EnvironmentComponentDetailsPanelProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const toastService = useToastService();
	const [panelWidth, setPanelWidth] = React.useState(ENVIRONMENT_COMPONENT_DETAILS_PANEL_DEFAULT_WIDTH);
	const [runtimeProfileOverride, setRuntimeProfileOverride] = React.useState<ComponentRuntimeProfileOverride>(environmentComponent.runtimeProfileOverride ?? {});
	const [isRuntimeProfileOverrideEnabled, setIsRuntimeProfileOverrideEnabled] = React.useState(false);
	const [isSavingRuntimeProfile, setIsSavingRuntimeProfile] = React.useState(false);
	const componentRuntimeProfile = component.runtimeProfile ?? DEFAULT_RUNTIME_PROFILE;

	React.useEffect(() => {
		const override = environmentComponent.runtimeProfileOverride ?? {};
		setRuntimeProfileOverride(override);
		setIsRuntimeProfileOverrideEnabled(
			(override.tier !== undefined && override.tier !== componentRuntimeProfile.tier) ||
			(override.minInstances !== undefined && override.minInstances !== componentRuntimeProfile.minInstances) ||
			(override.maxInstances !== undefined && override.maxInstances !== componentRuntimeProfile.maxInstances),
		);
	}, [componentRuntimeProfile.maxInstances, componentRuntimeProfile.minInstances, componentRuntimeProfile.tier, environmentComponent.runtimeProfileOverride]);

	const effectiveRuntimeProfile = React.useMemo(
		() => ({
			tier: runtimeProfileOverride.tier ?? componentRuntimeProfile.tier,
			minInstances: runtimeProfileOverride.minInstances ?? componentRuntimeProfile.minInstances,
			maxInstances: runtimeProfileOverride.maxInstances ?? componentRuntimeProfile.maxInstances,
		}),
		[componentRuntimeProfile, runtimeProfileOverride],
	);

	const isUsingComponentDefaultRuntimeProfile = !isRuntimeProfileOverrideEnabled;

	const handleRefresh = () => {
		if (onRefresh) {
			onRefresh();
		}
	};

	const handleDeleteAttributes = async (keys: string[]) => {
		if (!environment || keys.length === 0) return;

		try {
			// Delete all selected attributes
			for (const key of keys) {
				await client.removeComponentAttribute(environment.id, environmentComponent.id, key);
			}

			toastService.success(`${keys.length} attribute${keys.length !== 1 ? 's' : ''} removed successfully`);
			handleRefresh();
		} catch (error) {
			console.error('Error removing attributes:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove attributes: ${errorMessage}`);
			throw error;
		}
	};

	const handleSaveCustomAttributes = async (editedAttributes: { [key: string]: string }) => {
		if (!environment) return;

		try {
			// Get current custom attributes
			const currentCustomAttrs: { [key: string]: string } = {};
			if (environmentComponent.attributes) {
				Object.entries(environmentComponent.attributes).forEach(([key, value]) => {
					if (key.startsWith('Custom:')) {
						currentCustomAttrs[key] = String(value);
					}
				});
			}

			// Determine which attributes were deleted
			const deletedKeys = Object.keys(currentCustomAttrs).filter((key) => !(key in editedAttributes));

			// Delete removed attributes
			for (const key of deletedKeys) {
				await client.removeComponentAttribute(environment.id, environmentComponent.id, key);
			}

			const changeCount = deletedKeys.length;
			if (changeCount > 0) {
				toastService.success(`Custom attributes updated successfully (${changeCount} deletion${changeCount !== 1 ? 's' : ''}).`);
			} else {
				toastService.info('No changes to save.');
			}

			handleRefresh();
		} catch (error) {
			console.error('Error saving custom attributes:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to save custom attributes: ${errorMessage}`);
			throw error; // Re-throw to keep edit mode open
		}
	};

	const saveRuntimeProfile = async (runtimeProfile: ComponentRuntimeProfileOverride) => {
		if (!environment) {
			return;
		}

		try {
			setIsSavingRuntimeProfile(true);
			await client.setEnvironmentComponentRuntimeProfile(environment.id, component.id, { runtimeProfileOverride: runtimeProfile });
			toastService.success('Runtime profile saved. A component deployment has been queued.');
			handleRefresh();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to save runtime profile: ${errorMessage}`);
		} finally {
			setIsSavingRuntimeProfile(false);
		}
	};

	const handleSaveRuntimeProfile = async () => {
		if (isRuntimeProfileOverrideEnabled && effectiveRuntimeProfile.minInstances > effectiveRuntimeProfile.maxInstances) {
			toastService.error('Minimum instances cannot exceed maximum instances.');
			return;
		}

		await saveRuntimeProfile(isRuntimeProfileOverrideEnabled ? effectiveRuntimeProfile : {});
	};

	// Build service domain URL if environment has DomainName attribute
	const serviceDomainUrl = React.useMemo(() => {
		const domainName = environment?.attributes['DomainName'];
		const componentName = environmentComponent.attributes['Azure:ApplicationName'] || component.name;
		if (domainName && componentName) {
			return `https://${domainName}/${componentName}`;
		}
		return null;
	}, [environment, component.name, environmentComponent]);

	const referencingClients = React.useMemo(
		() =>
			clients
				.map((item) => ({
					item,
					environmentClient: environment?.clients?.find((environmentClient) => environmentClient.clientId === item.id),
					dependencies: item.dependencies?.filter((dependency) => dependency.dependsOnComponentId === component.id) ?? [],
				}))
				.filter((item) => item.dependencies.length > 0)
				.sort((left, right) => left.item.name.localeCompare(right.item.name)),
		[clients, component.id, environment?.clients],
	);

	const referencingApiKeys = React.useMemo(
		() =>
			apiKeys
				.map((item) => ({
					item,
					environmentApiKey: environment?.apiKeys?.find((environmentApiKey) => environmentApiKey.apiKeyId === item.id),
					dependencies: item.dependencies?.filter((dependency) => dependency.dependsOnComponentId === component.id) ?? [],
				}))
				.filter((item) => item.dependencies.length > 0)
				.sort((left, right) => left.item.name.localeCompare(right.item.name)),
		[apiKeys, component.id, environment?.apiKeys],
	);

	return (
		<SidePanelDetail
			title="Component Details"
			isLoading={false}
			isFetching={false}
			error={null}
			position="standalone"
			width={panelWidth}
			top={top}
			zIndex={1202}
			onClose={onClose}
			onRefresh={onRefresh ? handleRefresh : undefined}
			resizeStorageKey={ENVIRONMENT_COMPONENT_DETAILS_PANEL_STORAGE_KEY}
			defaultWidth={ENVIRONMENT_COMPONENT_DETAILS_PANEL_DEFAULT_WIDTH}
			minWidth={ENVIRONMENT_COMPONENT_DETAILS_PANEL_MIN_WIDTH}
			maxWidth={ENVIRONMENT_COMPONENT_DETAILS_PANEL_MAX_WIDTH}
			setWidth={setPanelWidth}
		>
			{/* Component Summary */}
			<Box sx={{ mb: 2 }}>
				{/* Component Name */}
				<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
					<Typography variant="body2" color="textSecondary">
						Name:
					</Typography>
					<Typography variant="body2" sx={{ flexGrow: 1 }}>
						{component.name}
					</Typography>
					<CopyToClipboardButton value={component.name} size="small" iconSize={16} title="Copy name to clipboard" edge={false} sx={{ p: 0.5 }} />
				</Stack>

				{/* Component ID */}
				<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
					<Typography variant="body2" color="textSecondary">
						ID:
					</Typography>
					<Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.75rem', fontFamily: 'monospace' }}>
						{component.id}
					</Typography>
					<CopyToClipboardButton value={component.id} size="small" iconSize={16} title="Copy ID to clipboard" edge={false} sx={{ p: 0.5 }} />
				</Stack>

				{/* Container Image Name */}
				<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
					<Typography variant="body2" color="textSecondary">
						Image:
					</Typography>
					<Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.75rem', fontFamily: 'monospace' }}>
						{component.containerImageName}
					</Typography>
					<CopyToClipboardButton
						value={component.containerImageName}
						size="small"
						iconSize={16}
						title="Copy image name to clipboard"
						edge={false}
						sx={{ p: 0.5 }}
					/>
				</Stack>

				{/* Deployed Image Version */}
				<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
					<Typography variant="body2" color="textSecondary">
						Version:
					</Typography>
					<Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.75rem', fontFamily: 'monospace' }}>
						{environmentComponent.containerImageVersion || component.containerImageVersion}
					</Typography>
					<CopyToClipboardButton
						value={environmentComponent.containerImageVersion || component.containerImageVersion}
						size="small"
						iconSize={16}
						title="Copy version to clipboard"
						edge={false}
						sx={{ p: 0.5 }}
					/>
				</Stack>

				{/* Component URL */}
				{environmentComponent.url && (
					<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
						<Typography variant="body2" color="textSecondary">
							URL:
						</Typography>
						<Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
							<Link
								href={environmentComponent.url}
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									wordBreak: 'break-all',
									flexShrink: 1,
									flexGrow: 1,
									fontSize: '0.875rem',
								}}
							>
								{environmentComponent.url}
							</Link>
							<IconButton size="small" onClick={() => window.open(environmentComponent.url, '_blank')} title="Open in new window" sx={{ p: 0.25 }}>
								<OpenInNew sx={{ fontSize: 14 }} />
							</IconButton>
						</Box>
					</Stack>
				)}

				{/* Context ID if available */}
				{component.contextId && (
					<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
						<Typography variant="body2" color="textSecondary">
							Context ID:
						</Typography>
						<Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.75rem', fontFamily: 'monospace' }}>
							{component.contextId}
						</Typography>
						<CopyToClipboardButton value={component.contextId} size="small" iconSize={16} title="Copy context ID to clipboard" edge={false} sx={{ p: 0.5 }} />
					</Stack>
				)}
			</Box>

			{component.contextId && environment?.id && <WebsiteOriginsEditor componentId={component.id} environmentId={environment.id} />}

			<Divider sx={{ my: 2 }} />

			<Box sx={{ mb: 2 }}>
				<Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
						Runtime profile
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{isUsingComponentDefaultRuntimeProfile ? 'Component default' : 'Environment override'}
					</Typography>
				</Stack>
				<Stack spacing={1.5}>
					<FormControlLabel
						control={
							<Checkbox
								checked={isRuntimeProfileOverrideEnabled}
								onChange={(event) => {
									setIsRuntimeProfileOverrideEnabled(event.target.checked);
									if (!event.target.checked) {
										setRuntimeProfileOverride({});
									}
								}}
							/>
						}
						label="Override component default"
					/>
					<Select
						size="small"
						value={effectiveRuntimeProfile.tier}
						disabled={!isRuntimeProfileOverrideEnabled}
						onChange={(event) => setRuntimeProfileOverride((current) => ({ ...current, tier: event.target.value as RuntimeProfileTier }))}
						inputProps={{ 'aria-label': 'Runtime profile tier' }}
					>
						{Object.values(RuntimeProfileTier).map((tier) => (
							<MenuItem key={tier} value={tier}>
								{tier}
							</MenuItem>
						))}
					</Select>
					<Stack direction="row" spacing={1.5}>
						<TextField
							fullWidth
							size="small"
							type="number"
							label="Min instances"
							value={effectiveRuntimeProfile.minInstances}
							disabled={!isRuntimeProfileOverrideEnabled}
							onChange={(event) => setRuntimeProfileOverride((current) => ({ ...current, minInstances: Math.max(0, Number(event.target.value)) }))}
							inputProps={{ min: 0, step: 1 }}
						/>
						<TextField
							fullWidth
							size="small"
							type="number"
							label="Max instances"
							value={effectiveRuntimeProfile.maxInstances}
							disabled={!isRuntimeProfileOverrideEnabled}
							onChange={(event) => setRuntimeProfileOverride((current) => ({ ...current, maxInstances: Math.max(0, Number(event.target.value)) }))}
							inputProps={{ min: 0, step: 1 }}
						/>
					</Stack>
					<Stack direction="row" justifyContent="flex-end" spacing={1}>
						<Button size="small" variant="contained" onClick={handleSaveRuntimeProfile} disabled={isSavingRuntimeProfile}>
							Save
						</Button>
					</Stack>
				</Stack>
			</Box>

			{/* Service Domain URL */}
			{serviceDomainUrl && (
				<>
					<Divider sx={{ my: 2 }} />
					<Box sx={{ mb: 2 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
							Service Domain
						</Typography>
						<Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
									<Link
										href={serviceDomainUrl}
										target="_blank"
										rel="noopener noreferrer"
										sx={{
											wordBreak: 'break-all',
											flexShrink: 1,
											flexGrow: 1,
											fontSize: '0.875rem',
											fontWeight: 'medium',
										}}
									>
										{serviceDomainUrl}
									</Link>
									<IconButton size="small" onClick={() => window.open(serviceDomainUrl, '_blank')} title="Open in new window" sx={{ p: 0.25 }}>
										<OpenInNew sx={{ fontSize: 14 }} />
									</IconButton>
								</Box>
								<CopyToClipboardButton
									value={serviceDomainUrl}
									size="small"
									iconSize={16}
									title="Copy service domain URL to clipboard"
									edge={false}
									sx={{ p: 0.5 }}
								/>
							</Stack>
						</Paper>
					</Box>
				</>
			)}

			<Divider sx={{ my: 2 }} />

			<ReferencingResourcesSection
				title="Referencing API Clients"
				emptyMessage="No API clients refer to this component"
				items={referencingClients.map(({ item, environmentClient, dependencies }) => ({
					id: item.id,
					icon: <Groups sx={{ fontSize: 16, color: 'primary.main' }} />,
					name: item.name,
					code: item.code,
					dependencies: dependencies.map((dependency) => dependency.targetType),
					isDeployed: !!environmentClient,
					onOpen: onClientSelect ? () => onClientSelect(item, environmentClient, environment) : undefined,
					openTitle: 'Open API client',
				}))}
			/>

			<Divider sx={{ my: 2 }} />

			<ReferencingResourcesSection
				title="Referencing API Keys"
				emptyMessage="No API keys refer to this component"
				items={referencingApiKeys.map(({ item, environmentApiKey, dependencies }) => ({
					id: item.id,
					icon: <VpnKey sx={{ fontSize: 16, color: 'primary.main' }} />,
					name: item.name,
					code: item.code,
					dependencies: dependencies.map((dependency) => `${dependency.accessChannel} ${dependency.targetType}`),
					isDeployed: !!environmentApiKey,
					onOpen: onApiKeySelect ? () => onApiKeySelect(item, environmentApiKey, environment) : undefined,
					openTitle: 'Open API key',
				}))}
			/>

			<Divider sx={{ my: 2 }} />

			{/* Render Environment Component Attributes */}
			<AttributesList
				attributes={environmentComponent.attributes}
				title="Attributes"
				emptyMessage="No attributes defined for this environment component"
				onSaveCustomAttributes={handleSaveCustomAttributes}
				onDeleteAttributes={handleDeleteAttributes}
			/>
		</SidePanelDetail>
	);
}
